import { useDroppable } from '@dnd-kit/core';
import { useRef, useState } from 'react';
import { Checkbox, checkboxClasses, IconButton } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import DeleteIcon from '@mui/icons-material/Delete';
import CloseIcon from '@mui/icons-material/Close';
import QuestionMarkIcon from '@mui/icons-material/QuestionMark';
import * as icons from "@mui/icons-material";
import { useTranslation } from "react-i18next";

const KanbanColumn = ({
    id,
    title,
    description,
    icon,
    items,
    setItems,
    allItems,
    setAllItems,
    renderItem,
    renderItemFilter,
    filter,
    filterAll,
    getAllExternalItems,
    onAddItem,
    onDeleteItems,
    onDeleteColumn,
    columnIdPrefix,
    elementsPerPage = 100,
    column,
}) => {
    const [searchTerm, setSearchTerm] = useState("");
    const [showColumn, setShowColumn] = useState(true);
    const [allChecked, setAllChecked] = useState(false);
    const [selectedIds, setSelectedIds] = useState([]);
    const [isSelected, setIsSelected] = useState(-1);
    const [currentPage, setCurrentPage] = useState(1);
    const [addMode, setAddMode] = useState(false);
    const [isDone, setIsDone] = useState(false);
    const columnRoot = useRef(document.getElementById(`gestion-admin-dialog`));
    const newRoot = useRef(document.getElementById(`gestion-admin-dialog-closed`));
    const thisElem = useRef(document.getElementById(id));

    const filteredItems = filter(items, searchTerm);
    const filteredAllItems = filterAll(allItems, items, column);
    const pagination = Math.ceil((filteredItems.length + 1) / elementsPerPage);
    const allPagination = Math.ceil((filteredAllItems.length + 1) / elementsPerPage);

    const { attributes, listeners, setNodeRef, isOver } = useDroppable({ id: id });
    const { t } = useTranslation();

    const toggleColumn = () => {
        columnRoot.current = document.getElementById(`gestion-admin-dialog`);
        newRoot.current = document.getElementById(`gestion-admin-dialog-closed`);
        thisElem.current = document.getElementById(id);

        try {
            if (showColumn) {
                columnRoot.current?.removeChild(thisElem.current);
                newRoot.current?.appendChild(thisElem.current);
            } else {
                newRoot.current?.removeChild(thisElem.current);
                columnRoot.current?.appendChild(thisElem.current);
            }
        } catch (error) { }
    };

    const handleCheckAll = (e) => {
        setAllChecked(e.target.checked);
        if (allChecked) {
            setSelectedIds([]);
        }
    };

    const handleDelete = async () => {
        try {
            const newItems = await onDeleteItems(selectedIds, items);
            await setItems(newItems);
            setSelectedIds([]);
            setIsSelected(-1);
            setAllChecked(false);
        } catch (error) {
            console.error(error.message);
        }
    };

    const handleDeleteColumn = async () => {
        try {
            onDeleteColumn(column);
        } catch (error) {
            console.error(error.message);
        }
    };

    const handleAddItem = async (item) => {
        try {
            const localItems = await onAddItem(item, items);
            await setItems(localItems);
            await setAddMode(false);
        } catch (error) {
            console.error(error.message);
        }
    };

    if (isSelected !== -1) {
        const found = selectedIds.indexOf(isSelected);

        if (found === -1) {
            selectedIds.push(isSelected);
        } else {
            selectedIds.splice(found, 1);
        }
        setIsSelected(-1);
    }

    if (addMode && getAllExternalItems && !isDone) {
        const fetch = async () => {
            const vals = await getAllExternalItems();
            setAllItems(vals);
        };
        fetch();
        setIsDone(true);
    }

    const Icon = icon ? icons[icon] : null;
    
    return (
        <div className={`${columnIdPrefix}-dialog-column gestion-admin-dialog-column`} style={{ maxHeight: showColumn ? '' : 'fit-content' }} id={id}>
            <div className={`${columnIdPrefix}-dialog-column-top gestion-admin-dialog-column-top`}>
                <div className={`${columnIdPrefix}-dialog-column-title gestion-admin-dialog-column-title`}>
                    <p className={`${columnIdPrefix}-dialog-column-title-icon gestion-admin-dialog-column-title-icon`}>{Icon ? <Icon /> : ""}</p>
                    &nbsp;&nbsp;
                    <p className={`${columnIdPrefix}-dialog-column-title-h gestion-admin-dialog-column-title-h`}>{title} </p>
                    &nbsp;&nbsp;
                    <p className={`${columnIdPrefix}-dialog-column-title-h gestion-admin-dialog-column-title-h`} title={description}><QuestionMarkIcon size="small" /></p>
                    &nbsp;&nbsp;
                    <p className={`${columnIdPrefix}-dialog-column-title-nb gestion-admin-dialog-column-title-nb`}>{(!addMode ? filteredItems.length : filteredAllItems.length)}</p>
                </div>
                <div className={`${columnIdPrefix}-dialog-column-buttons gestion-admin-dialog-column-buttons`}>
                    <IconButton onClick={() => { setShowColumn(!showColumn); toggleColumn(); }} disabled={addMode}>
                        {showColumn ? <VisibilityIcon /> : <VisibilityOffIcon />}
                    </IconButton>
                    {showColumn && !addMode && (
                        <IconButton className={`${columnIdPrefix}-dialog-column-icon gestion-admin-dialog-column-icon`} size="small" onClick={e => setAddMode(true)} disableTouchRipple>
                            <AddIcon style={{ color: 'white' }} fontSize="medium" />
                        </IconButton>
                    )}
                    {showColumn && addMode && (
                        <IconButton className={`${columnIdPrefix}-dialog-column-icon gestion-admin-dialog-column-icon`} size="small" onClick={e => setAddMode(false)} disableTouchRipple>
                            <CloseIcon style={{ color: 'white' }} fontSize="medium" />
                        </IconButton>
                    )}
                    {onDeleteColumn && typeof onDeleteColumn === 'function' && (
                        <IconButton
                            aria-label="delete"
                            onClick={handleDeleteColumn}
                            color="error"
                        >
                            <DeleteIcon />
                        </IconButton>
                    )}
                </div>
            </div>

            {addMode === false &&
                <>
                    <>
                        {showColumn &&
                            <div className={`${columnIdPrefix}-dialog-column-search gestion-admin-dialog-column-search`}>
                                <input
                                    type="text"
                                    placeholder="Rechercher..."
                                    value={searchTerm}
                                    onChange={(e) => setSearchTerm(e.target.value)}
                                />
                                <div className={`${columnIdPrefix}-dialog-column-delete gestion-admin-dialog-column-delete`}>
                                    <Checkbox
                                        checked={allChecked || (filteredItems.length === selectedIds.length && filteredItems.length > 0)}
                                        sx={{
                                            [`&, &.${checkboxClasses.checked}`]: {
                                                color: 'var(--peoplmap-primary)',
                                            },
                                        }}
                                        onChange={(e) => handleCheckAll(e)}
                                    />
                                    <IconButton
                                        aria-label="delete"
                                        onClick={handleDelete}
                                        disabled={!allChecked && selectedIds.length === 0}
                                        color="error"
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </div>
                            </div>
                        }
                        {showColumn &&
                            <div
                                ref={setNodeRef}
                                className={`${columnIdPrefix}-dialog-column-content gestion-admin-dialog-column-content`}
                                style={{
                                    border: isOver ? '1px dashed gray' : '1px solid transparent',
                                    borderRadius: '15px',
                                }}
                                {...listeners}
                                {...attributes}
                            >
                                {filteredItems.length > 0 ? (
                                    filteredItems
                                        .slice((currentPage - 1) * elementsPerPage, currentPage * elementsPerPage)
                                        .map(item => {
                                            if (allChecked && selectedIds.indexOf(item.id) === -1) { selectedIds.push(item.id) }
                                            return renderItem(item, setIsSelected, selectedIds, columnIdPrefix)
                                        })
                                ) : (
                                    <div> {t("NO_ELEMENTS_FOUND")} </div>
                                )}
                            </div>
                        }
                    </>
                </>
            }

            {addMode && allItems.length > 0 && (
                <div className={`${columnIdPrefix}-dialog-add-mode gestion-admin-dialog-add-mode`}>
                    {renderItemFilter(filteredAllItems, handleAddItem, setIsSelected, selectedIds, columnIdPrefix, currentPage, elementsPerPage)}
                </div>
            )}

            {showColumn &&
                <div className={`${columnIdPrefix}-dialog-pagination gestion-admin-dialog-pagination`}>
                    <button
                        type="button"
                        className={`${columnIdPrefix}-dialog-pagination-previous gestion-admin-dialog-pagination-previous`}
                        onClick={() => setCurrentPage(currentPage - 1)}
                        style={{ filter: currentPage === 1 ? 'grayscale(100%)' : 'none' }}
                        disabled={currentPage === 1}
                    >
                        {" < "}
                    </button>
                    <span className={`${columnIdPrefix}-dialog-pagination-current gestion-admin-dialog-pagination-current`}>
                        {" " + currentPage + " "}
                    </span>
                    <button
                        type="button"
                        className={`${columnIdPrefix}-dialog-pagination-next gestion-admin-dialog-pagination-next`}
                        onClick={() => setCurrentPage(currentPage + 1)}
                        style={{ filter: (!addMode ? pagination : allPagination) === currentPage ? 'grayscale(100%)' : 'none' }}
                        disabled={(!addMode ? pagination : allPagination) === currentPage}
                    >
                        {" > "}
                    </button>
                </div>
            }
        </div>
    );
};

export default KanbanColumn;
